"use strict";

import { LoginToCallCenterResponse } from "@/Common/Api/CallCenter/Responses/LoginToCallCenterResponse";
import { Client } from "@/Common/Api/Client";
import { RealTimeNotificationResourceType } from "@/Common/Api/RealTimeNotification/Dto/RealTimeNotificationResourceType";
import { realTimeNotificationClient } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SendPingCallCenterResponse } from "../Api/CallCenter/Responses/SendPingCallCenterResponse";
import { ClientResponse } from "../Api/ClientResponse";
import { ErrorMessage } from "../Api/Dto/ErrorMessage";
import { CallCenterUserConnectionState } from "../Models/CallCenter/CallCenterUserConnectionState";

type CallCenterLoginOptions = {
    isInitial?: boolean;
    isUserInitiated?: boolean;
};

const LoginReattemptDelaySeconds = 60;
const PingFrequencySeconds = 60;

class CallCenterHelper {
    private loginRetryTimeoutRef: NodeJS.Timeout;
    private pingTimeoutRef: NodeJS.Timeout;
    private readonly boundPingAsync: () => Promise<void>;

    constructor() {
        this.boundPingAsync = this.pingAsync.bind(this);
        this.loginRetryTimeoutRef = null;
        this.pingTimeoutRef = null;
    }

    public async loginAsync(options: Partial<CallCenterLoginOptions> = null): Promise<void> {
        const isInitial = options?.isInitial ?? false;
        const isUserInitiated = options?.isUserInitiated ?? false;

        this.updateStateForLogin();
        await this.subscribeToRealTimeEventsAsync();

        // subscribe to SignalR incase we receive event during login
        await realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.CallCenter, [sessionStore.userId]);

        const response = await Client.postAsync<LoginToCallCenterResponse>(
            ServerOperations.Server.Myle_CallCenter_Login,
            {
                sessionId: sessionStore.CallCenter_SessionId.toString(),
                isInitialLogin: isInitial
            }
        );

        if (response.errors != null && response.errors.length > 0) {
            await this.handleLoginErrorsAsync(isInitial, isUserInitiated, response);
            return;
        }
        else {
            sessionStore.CallCenter_LastLoginError = null;
        }

        if (!this.isUserState(CallCenterUserConnectionState.Connecting)) {
            // user cancelled login
            return;
        }

        this.updateStateFromLoginResponse(isInitial, response);
        await this.unsubscribeFromRealTimeEventsIfNotConnectedAsync();

        if (this.shouldNotShowLoginResult(isInitial)) {
            return;
        }

        if (!this.isUserState(CallCenterUserConnectionState.Connected)) {
            if (isUserInitiated) {
                sessionStore.showPopup(cacheStore.GetString("CallCenter_AnotherTabAlreadyLoggedInToast"));
            }
            else {
                displayToast(MessageToast.Warning, cacheStore.GetString("CallCenter_AnotherTabAlreadyLoggedInToast"));
            }
            return;
        }

        displayToast(MessageToast.Success, cacheStore.GetString("CallCenter_LoginSuccessToast"));

        this.schedulePingInSeconds(PingFrequencySeconds);
    }

    public async logoutWithBeaconAsync(): Promise<void> {
        return this.logoutInternalAsync(true);
    }

    public async logoutAsync(): Promise<void> {
        return this.logoutInternalAsync(false);
    }

    public closeLoginWindow(): void {
        sessionStore.CallCenter_ShowLoginAccountPopup = false;
        sessionStore.EmitChangeToListeners();
    }

    public formatErrorMessages(errors: ErrorMessage[], currentProvider: string): ErrorMessage[] {
        if (errors == null || errors.length === 0) {
            return errors;
        }
        return errors.map((error: ErrorMessage) => {
            if (error.VocabularyKey === "CallCenter_InvalidUserCredentials"
                || error.VocabularyKey === "CallCenter_InvalidIntegrationCredentials") {
                return new ErrorMessage({
                    Code: error.Code,
                    Message: cacheStore.GetString(error.VocabularyKey, { providerType: currentProvider }),
                    Type: error.Type
                });
            }
            return error;
        });
    }

    public is10DigitPhoneNumber(phoneNumber: string): boolean {
        // check if the phone number is 10 digits long
        const regex = /^\d{10}$/;
        return regex.test(phoneNumber);
    }

    private async logoutInternalAsync(sendBeacon: boolean): Promise<void> {
        if (!this.isUserState(CallCenterUserConnectionState.Disconnected)) {
            this.updateStateLoggedOut();
            this.cancelPingTimeout();
            const channelPromise = this.unsubscribeFromRealTimeEventsIfNotConnectedAsync();

            if (sendBeacon) {
                Client.sendBeacon(ServerOperations.Server.Myle_CallCenter_Logout, { sessionId: sessionStore.CallCenter_SessionId.toString() });
            }
            else {
                await Client.postAsync(ServerOperations.Server.Myle_CallCenter_Logout, { sessionId: sessionStore.CallCenter_SessionId.toString() });
            }

            await channelPromise;
        }
    }

    // Function that sends a ping request to the server, with following ping scheduled after pingFrequencySeconds amount of seconds
    private async pingAsync(): Promise<void> {
        if (this.isUserState(CallCenterUserConnectionState.Connected)) {
            const response = await Client.postAsync<SendPingCallCenterResponse>(ServerOperations.Server.Myle_CallCenter_Ping, { sessionId: sessionStore.CallCenter_SessionId.toString() });
            if (response.errors != null && response.errors.length > 0) {
                this.schedulePingInSeconds(PingFrequencySeconds / 2);
                return;
            }
            if (!response.data.IsLoggedIn) {
                this.loginAsync();
                return;
            }
            this.schedulePingInSeconds(PingFrequencySeconds);
        }
    }

    private async subscribeToRealTimeEventsAsync(): Promise<void> {
        await realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.CallCenter, [sessionStore.userId]);
    }

    private async unsubscribeFromRealTimeEventsIfNotConnectedAsync(): Promise<void> {
        if (sessionStore.CallCenter_UserConnectionState !== CallCenterUserConnectionState.Connected) {
            await realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.CallCenter, []);
        }
    }

    private shouldNotShowLoginResult(isInitial: boolean): boolean {
        return sessionStore.CallCenter_ShowLoginAccountPopup
        || (isInitial && sessionStore.CallCenter_Credentials == null);
    }

    private updateStateForLogin(): void {
        this.cancelLoginRetryTimeout();
        this.cancelPingTimeout();
        sessionStore.CallCenter_UserConnectionState = CallCenterUserConnectionState.Connecting;
        sessionStore.EmitChangeToListeners();
    }

    private updateStateLoggedOut(): void {
        sessionStore.CallCenter_UserConnectionState = CallCenterUserConnectionState.Disconnected;
        sessionStore.CallCenter_LastLoginError = null;
        sessionStore.EmitChangeToListeners();
    }

    private updateStateFromLoginResponse(isInitial: boolean, response: ClientResponse<LoginToCallCenterResponse>): void {
        sessionStore.CallCenter_Credentials = response.data.Credentials;
        sessionStore.CallCenter_UserConnectionState = response.data.IsLoggedIn ? CallCenterUserConnectionState.Connected : CallCenterUserConnectionState.Disconnected;
        sessionStore.CallCenter_AutoOpenPatientOnSingleMatch = response.data.AutoOpenPatientOnSingleMatch;
        if (isInitial
            && response.data.IsUserActive
            && response.data.AskForAccountAtLogin
            && response.data.Credentials != null) {
            sessionStore.CallCenter_ShowLoginAccountPopup = true;
        }
        sessionStore.EmitChangeToListeners();
    }

    private async handleLoginErrorsAsync(isInitial: boolean, isUserInitiated: boolean, response: ClientResponse<LoginToCallCenterResponse>): Promise<void> {
        // remove subscription to SignalR
        await realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.CallCenter, []);

        let retryLogin = false;

        if (isUserInitiated) {
            sessionStore.showPopup(this.formatErrorMessages(response.errors, response.data.Credentials?.Provider));
        }
        else {
            if (!response.errors.some((error: ErrorMessage) => error.VocabularyKey === "CallCenter_InvalidUserCredentials")) {
                retryLogin = true;
            }
            displayToast(MessageToast.Error, cacheStore.GetString("CallCenter_LoginFailureToast"));
        }

        if (retryLogin) {
            this.scheduleLoginRetryInSeconds(LoginReattemptDelaySeconds, { isInitial, isUserInitiated });
        }
        else {
            sessionStore.CallCenter_UserConnectionState = CallCenterUserConnectionState.Disconnected;
        }
        sessionStore.CallCenter_LastLoginError = response.errors;
        sessionStore.CallCenter_UserConnectionState = CallCenterUserConnectionState.Disconnected;
        sessionStore.EmitChangeToListeners();
    }

    private isUserState(state: CallCenterUserConnectionState): boolean {
        return sessionStore.CallCenter_UserConnectionState === state;
    }

    private schedulePingInSeconds(seconds: number): void {
        this.pingTimeoutRef = setTimeout(this.boundPingAsync, seconds * 1000);
    }

    private scheduleLoginRetryInSeconds(seconds: number, loginOptions: Partial<CallCenterLoginOptions>): void {
        this.loginRetryTimeoutRef = setTimeout(() => this.loginAsync(loginOptions), seconds * 1000);
    }

    private cancelPingTimeout(): void {
        if (this.pingTimeoutRef != null) {
            clearTimeout(this.pingTimeoutRef);
            this.pingTimeoutRef = null;
        }
    }

    private cancelLoginRetryTimeout(): void {
        if (this.loginRetryTimeoutRef != null) {
            clearTimeout(this.loginRetryTimeoutRef);
            this.loginRetryTimeoutRef = null;
        }
    }
}

const callCenterHelperSingleton = new CallCenterHelper();

export {
    callCenterHelperSingleton as CallCenterHelper
};
